<template>
  <div class="signature-container flex flex-col items-center w-full p-4">
    <img v-if="displayMode !== 'edit' && existingSignature" :src="existingSignature" alt="Signature" class="signature-img w-full max-w-lg"/>
    <div v-if="displayMode === 'locked' && !existingSignature"
         class="w-full max-w-lg py-6 text-center text-sm text-gray-500 border border-gray-300 rounded">
      Not provided.
    </div>

    <template v-if="displayMode === 'edit'">
      <canvas ref="signatureCanvas" class="signature-canvas w-full max-w-lg h-52 border border-gray-300"></canvas>
      <div class="buttons mt-4 flex flex-wrap gap-2 justify-end w-full">
        <AppButton v-if="showSketchTools" color="gray" label="Undo" :icon="true" @click="undo" :disabled="!canUndo">
          <template v-slot:icon>
            <Icon iconType="undo"/>
          </template>
        </AppButton>
        <AppButton v-if="showSketchTools" color="gray" label="Redo" :icon="true" @click="redo" :disabled="!canRedo">
          <template v-slot:icon>
            <Icon iconType="redo"/>
          </template>
        </AppButton>
        <AppButton v-if="showSketchTools" :color="isErasing ? 'indigo' : 'gray'" :label="isErasing ? 'Erasing' : 'Erase'" :icon="true" @click="toggleEraser">
          <template v-slot:icon>
            <Icon iconType="eraser"/>
          </template>
        </AppButton>
        <AppButton color="gray" label="Clear" :icon="true" @click="clearSignature" :disabled="isEmpty">
          <template v-slot:icon>
            <Icon iconType="close"/>
          </template>
        </AppButton>
        <AppButton v-if="existingSignature" color="gray" label="Cancel" @click="cancelEdit"/>
        <AppButton color="green" label="Upload" @click="uploadSignature" :icon="true" :disabled="isEmpty">
          <template v-slot:icon>
            <Icon iconType="upload" class="mr-2"/>
          </template>
        </AppButton>
      </div>
    </template>

    <div v-if="displayMode === 'view' && allowRedraw" class="mt-3">
      <AppButton color="gray" label="Edit" :icon="true" @click="startEditing">
        <template v-slot:icon>
          <Icon iconType="edit"/>
        </template>
      </AppButton>
    </div>
  </div>
</template>
<script>
import SignaturePad from 'signature_pad';
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  mixins: [notificationMixin],
  components: {Icon, AppButton},

  props: {
    uploadRefId: {
      type: String,
      required: true,
    },
    uploadSignatureF: {
      type: Function,
      required: true,
    },
    getSignatureF: {
      type: Function,
      required: true,
    },
    documentCategory: {
      type: String,
      default: 'signatures',
    },
    documentRole: {
      type: String,
      default: 'signature',
    },
    /** Optional — when provided, the superseded document is deleted right after a
     *  redraw is uploaded, so repeated edits don't pile up documents. Without it,
     *  redraws just keep adding new documents (the list read only ever shows [0]). */
    deleteSignatureF: {
      type: Function,
      default: null,
    },
    /** Once locked (e.g. certificate marked Complete), no editing UI at all — view only. */
    locked: {
      type: Boolean,
      default: false,
    },
    /** Allow going back into edit mode after a signature/sketch already exists. Off by
     *  default — a real staff/contact signature shouldn't be casually redrawn. */
    allowRedraw: {
      type: Boolean,
      default: false,
    },
    /** Show Undo/Redo/Eraser alongside Clear/Upload — only meaningful once allowRedraw
     *  makes repeated editing possible in the first place. */
    showSketchTools: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      signaturePad: null,
      existingSignature: null,
      existingDocumentId: null,
      isEditing: false,
      isEmpty: true,
      isErasing: false,
      redoStack: [],
    };
  },

  computed: {
    displayMode() {
      if (this.locked) return 'locked';
      if (this.existingSignature && !this.isEditing) return 'view';
      return 'edit';
    },
    canUndo() {
      return !this.isEmpty;
    },
    canRedo() {
      return this.redoStack.length > 0;
    },
  },

  async mounted() {
    await this.loadFiles();
    this.isEditing = !this.existingSignature;
    if (this.displayMode === 'edit') {
      await this.setupCanvas();
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setCanvasSize);
  },

  watch: {
    async displayMode(mode) {
      if (mode === 'edit') {
        await this.setupCanvas();
      } else {
        window.removeEventListener('resize', this.setCanvasSize);
      }
    },
  },

  methods: {
    async setupCanvas() {
      await this.$nextTick();
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) return;

      this.signaturePad = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
      });
      this.signaturePad.addEventListener('endStroke', this.onStrokeEnd);
      this.isEmpty = true;
      this.redoStack = [];

      this.setCanvasSize();
      window.addEventListener('resize', this.setCanvasSize);
    },
    onStrokeEnd() {
      this.isEmpty = this.signaturePad.isEmpty();
      // A fresh stroke invalidates whatever was undone before it — standard undo/redo semantics.
      this.redoStack = [];
    },
    setCanvasSize() {
      const canvas = this.$refs.signatureCanvas;
      if (canvas) {
        // Save current content
        const dataUrl = canvas.toDataURL();

        // Set the canvas dimensions to match the container
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;

        // Restore the content
        const img = new Image();
        img.src = dataUrl;
        img.onload = () => {
          const context = canvas.getContext('2d');
          context.fillStyle = 'rgb(255, 255, 255)'; // Ensure the background color remains white
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(img, 0, 0);
        };
      }
    },
    clearSignature() {
      this.signaturePad.clear();
      this.isEmpty = true;
      this.redoStack = [];
    },
    undo() {
      if (!this.signaturePad || this.isEmpty) return;
      const data = this.signaturePad.toData();
      const removed = data.pop();
      if (removed) {
        this.redoStack.push(removed);
      }
      this.signaturePad.fromData(data);
      this.isEmpty = this.signaturePad.isEmpty();
    },
    redo() {
      if (!this.signaturePad || this.redoStack.length === 0) return;
      const restored = this.redoStack.pop();
      const data = this.signaturePad.toData();
      data.push(restored);
      this.signaturePad.fromData(data);
      this.isEmpty = this.signaturePad.isEmpty();
    },
    toggleEraser() {
      this.isErasing = !this.isErasing;
      if (!this.signaturePad) return;
      this.signaturePad.penColor = this.isErasing ? '#ffffff' : 'black';
      this.signaturePad.minWidth = this.isErasing ? 8 : 0.5;
      this.signaturePad.maxWidth = this.isErasing ? 16 : 2.5;
    },
    startEditing() {
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
      this.isErasing = false;
    },

    async uploadSignature() {
      if (!this.uploadRefId) {
        this.notifyWarning("Cannot add new files before saving this item.");
        return;
      }

      if (this.signaturePad.isEmpty()) {
        this.notifyError('Please provide a signature before uploading.');
        return;
      }

      try {
        const signatureImage = this.signaturePad.toDataURL('image/png');
        const blob = await (await fetch(signatureImage)).blob();
        const file = new File([blob], 'signature.png', {type: 'image/png'});

        const fileToUpload = [{
          name: file.name,
          content_type: file.type,
          visibility: "Internal",
          category: this.documentCategory,
          unique_id: this.generateUniqueId(),
          requires_thumbnail: false,
          document_role: this.documentRole,
        }];


        const uploadResults = await this.uploadSignatureF(fileToUpload, this.uploadRefId);

        if (uploadResults && uploadResults.length > 0) {
          const firstUploadResult = uploadResults[0];
          if (firstUploadResult.access_url) {
            await this.uploadFileToBlob(firstUploadResult.access_url, file);

            if (firstUploadResult.document_id) {
              await this.DocumentService.updateDocument(firstUploadResult.document_id, {
                is_uploaded: true,
              });
            }

            // Replace semantics: remove whatever this redraw superseded so repeated
            // edits don't pile up documents against the same ref.
            if (this.deleteSignatureF && this.existingDocumentId) {
              try {
                await this.deleteSignatureF(this.uploadRefId, this.existingDocumentId);
              } catch (error) {
                console.error("Error removing previous version:", error);
              }
            }

            this.notifySuccess("Signature uploaded successfully.");
            this.isErasing = false;
            await this.loadFiles();
            this.isEditing = false;
          } else {
            this.notifyError("Upload failed: No access URL returned for the first result.");
          }
        } else {
          this.notifyError("Upload failed: No results returned.");
        }
      } catch (error) {
        console.error("Upload error:", error);
        this.notifyError("There was an error uploading the signature.");
      }
    },
    generateUniqueId() {
      return `id_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    },
    async loadFiles() {
      try {
        if (!this.uploadRefId) return;
        const files = await this.getSignatureF(this.uploadRefId, this.documentRole);
        if (files.length > 0) {
          this.existingSignature = files[0].access_url;
          this.existingDocumentId = files[0].document_id || files[0].documentId || null;
        } else {
          this.existingSignature = null;
          this.existingDocumentId = null;
        }
      } catch (error) {
        console.error("Error loading files:", error);
      }
    },
    async uploadFileToBlob(sasUrl, file) {
      const url = new URL(sasUrl);
      const accountUrl = url.origin;
      const sasToken = url.search;
      const pathParts = url.pathname.split("/").filter(p => p.trim() !== "");
      const containerName = pathParts[0];
      const blobName = pathParts.slice(1).join("/");
      const blobService = AzureStorage.Blob.createBlobServiceWithSas(accountUrl, sasToken);

      return new Promise((resolve, reject) => {
        blobService.createBlockBlobFromBrowserFile(containerName, blobName, file, {blockSize: file.size}, (error, result) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        });
      });
    },
  },
};
</script>


<style scoped>
.signature-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
}

.signature-canvas {
  margin-bottom: 10px;
  border: 1px solid #ccc;
}

.buttons {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
}

button {
  padding: 5px 10px;
  cursor: pointer;
}

</style>
